import * as React from 'react';
import { cn } from '@/lib/cn';
import { withAuthToken } from '@/lib/api';

interface AvatarProps {
  src?: string | null;
  name?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClass = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-xl',
};

const initials = (name?: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
};

const colorFor = (name?: string | null): string => {
  const palettes = [
    'from-violet-500 to-fuchsia-500',
    'from-rose-500 to-orange-500',
    'from-emerald-500 to-teal-500',
    'from-blue-500 to-cyan-500',
    'from-amber-500 to-pink-500',
    'from-indigo-500 to-purple-500',
  ];
  if (!name) return palettes[0]!;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return palettes[Math.abs(h) % palettes.length]!;
};

export const Avatar = ({ src, name, size = 'md', className }: AvatarProps) => {
  const cls = cn(
    'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold text-white shadow-soft',
    sizeClass[size],
    !src && `bg-gradient-to-br ${colorFor(name)}`,
    className,
  );
  if (src) {
    // /uploads/* URLs are protected — append the auth token automatically so
    // every consumer doesn't have to wrap manually. Absolute URLs and blob:
    // previews pass through unchanged.
    const finalSrc =
      src.startsWith('blob:') || /^https?:\/\//i.test(src) ? src : withAuthToken(src);
    return <img src={finalSrc} alt={name ?? ''} className={cls} />;
  }
  return <span className={cls}>{initials(name)}</span>;
};
